import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useToast } from "../../../components/ui";
import { ApiError } from "../../../lib/http";
import {
  fetchPlanDurations,
  fetchPlanSuggestions,
} from "../services/planCatalogService";
import type {
  PlanDuration,
  PlanSuggestion,
} from "../types/brand.types";

/**
 * Loading, ready, or the request came back with nothing usable. Tracked per
 * list rather than for the pair: they are separate calls, and a durations
 * endpoint that is down is no reason to hold the name field shut.
 */
export type PlanOptionsStatus = "loading" | "ready" | "failed";

/** One list, plus the strings the dropdown actually renders. */
export interface PlanOptionList<T> {
  /** Rows as the API sent them, id included. */
  options: T[];
  /** Just the text of each, which is what a dropdown takes. */
  values: string[];
  status: PlanOptionsStatus;
}

export interface PlanOptions {
  suggestions: PlanOptionList<PlanSuggestion>;
  durations: PlanOptionList<PlanDuration>;
  /**
   * Runs both calls again.
   *
   * Only ever called from something the user pressed on purpose. Nothing about
   * opening, closing or reaching for a dropdown may call it: the lists are
   * fetched once when the tab opens, and a control that refetched on every
   * interaction would put a request on the wire each time someone changed
   * their mind about a field.
   */
  reload: () => void;
}

const NO_SUGGESTIONS: PlanSuggestion[] = [];
const NO_DURATIONS: PlanDuration[] = [];

const failureMessage = (error: unknown) =>
  error instanceof ApiError
    ? error.message
    : "We could not reach the server. Check your connection and try again.";

/**
 * The lists the create-plan dialog picks from, fetched together when the
 * Plans tab opens and reported one field at a time.
 *
 * They are not cached between mounts. They are small, they change from under
 * us whenever the backend's are edited, and the tab is opened rarely enough
 * that a request per visit is the cheaper trade than a stale list.
 *
 * A failed suggestions call is survivable in a way most lookup failures are
 * not: the name field can still be typed into through "Add custom plan", so
 * the dialog stays usable on an empty list.
 *
 * Must be used inside a Clerk provider and an active session: both endpoints
 * are 401 without a bearer token.
 */
export function usePlanOptions(): PlanOptions {
  const { getToken } = useAuth();
  const toast = useToast();

  // Clerk hands back a fresh `getToken` on some renders, and the load below
  // must not re-run for that - a fetch per render is a loop, and the tab
  // re-renders on every keystroke in its search box. The effect reads the
  // current one through this rather than depending on its identity.
  const getTokenRef = useRef(getToken);
  useEffect(() => {
    getTokenRef.current = getToken;
  }, [getToken]);

  const [suggestions, setSuggestions] = useState<PlanSuggestion[]>(
    NO_SUGGESTIONS
  );
  const [durations, setDurations] = useState<PlanDuration[]>(NO_DURATIONS);
  const [status, setStatus] = useState<
    Record<"suggestions" | "durations", PlanOptionsStatus>
  >({ suggestions: "loading", durations: "loading" });

  // Bumped by `reload`, which is the whole of what re-runs the effect.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    // A retry keeps whatever already loaded on screen while it runs: only the
    // status goes back to loading, so a working field does not blink empty.
    setStatus({ suggestions: "loading", durations: "loading" });

    const getCurrentToken = () => getTokenRef.current();

    // `allSettled`, not `all`: one endpoint failing must not throw away the
    // list the other returned.
    void Promise.allSettled([
      fetchPlanSuggestions(getCurrentToken, controller.signal),
      fetchPlanDurations(getCurrentToken, controller.signal),
    ]).then(([suggested, lengths]) => {
      // Unmounted, or a retry has already replaced this run.
      if (controller.signal.aborted) return;

      if (suggested.status === "fulfilled") setSuggestions(suggested.value);
      else setSuggestions(NO_SUGGESTIONS);

      if (lengths.status === "fulfilled") setDurations(lengths.value);
      else setDurations(NO_DURATIONS);

      setStatus({
        suggestions: suggested.status === "fulfilled" ? "ready" : "failed",
        durations: lengths.status === "fulfilled" ? "ready" : "failed",
      });

      // The first failure is the one reported: they go down together, for the
      // same reason, and a second identical toast says nothing extra.
      const failure = [suggested, lengths].find(
        (result) => result.status === "rejected"
      );
      if (failure?.status === "rejected") {
        console.error("Plan options failed to load:", failure.reason);
        toast.error(
          "Could not load the plan options",
          failureMessage(failure.reason)
        );
      }
    });

    return () => controller.abort();
  }, [toast, attempt]);

  const reload = useCallback(() => setAttempt((current) => current + 1), []);

  return useMemo(
    () => ({
      suggestions: {
        options: suggestions,
        values: suggestions.map((suggestion) => suggestion.name),
        status: status.suggestions,
      },
      durations: {
        options: durations,
        values: durations.map((duration) => duration.label),
        status: status.durations,
      },
      reload,
    }),
    [suggestions, durations, status, reload]
  );
}

export default usePlanOptions;
